import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Truck } from "lucide-react";
import { EbikLogo } from "@/components/EbikLogo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/warehousing", label: "Warehousing" },
  { to: "/cross-border", label: "Cross-Border" },
  { to: "/sustainability", label: "Sustainability" },
  { to: "/contact", label: "Contact" },
];

export function SiteLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, isStaff } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 dark:bg-slate-950/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-[68px]">
          <Link to="/" data-testid="nav-logo"><EbikLogo /></Link>
          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-primary" : "text-slate-600 dark:text-slate-300 hover:text-primary"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/track")} data-testid="nav-track-btn">Track</Button>
            {user ? (
              <Button size="sm" className="bg-primary hover:bg-red-700" onClick={() => nav(isStaff ? "/app/control-tower" : "/app")} data-testid="nav-portal-btn">
                My Portal
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => nav("/login")} data-testid="nav-login-btn">Sign In</Button>
                <Button size="sm" className="bg-primary hover:bg-red-700" onClick={() => nav("/quote")} data-testid="nav-quote-btn">Request a Quote</Button>
              </>
            )}
          </div>
          <button className="xl:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="xl:hidden border-t border-border bg-background px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-md text-sm font-medium hover:bg-muted">{n.label}</NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" variant="outline" onClick={() => { setOpen(false); nav("/track"); }}>Track</Button>
              <Button className="flex-1 bg-primary" onClick={() => { setOpen(false); nav(user ? "/app" : "/login"); }}>{user ? "Portal" : "Sign In"}</Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#0B0F17] text-slate-300 mt-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
          <div>
            <EbikLogo variant="dark" />
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Technology-driven freight and logistics connecting businesses across South Africa and the Southern African region.
            </p>
            <p className="mt-4 text-xs font-mono text-slate-500">Cape Town HQ · Johannesburg Hub · Durban Terminal</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Services</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/services" className="hover:text-primary">Full Truckload</Link></li>
              <li><Link to="/services" className="hover:text-primary">Cross-Border</Link></li>
              <li><Link to="/warehousing" className="hover:text-primary">Warehousing</Link></li>
              <li><Link to="/services" className="hover:text-primary">Freight Forwarding</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/about" className="hover:text-primary">About Ebik</Link></li>
              <li><Link to="/sustainability" className="hover:text-primary">Sustainability</Link></li>
              <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Get Started</h4>
            <div className="space-y-2">
              <Button className="w-full bg-primary hover:bg-red-700" onClick={() => nav("/quote")}>Request a Quote</Button>
              <Button variant="outline" className="w-full border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => nav("/track")}>
                <Truck size={16} className="mr-2" /> Track Shipment
              </Button>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500 font-mono">
          © 2026 Ebik Logistics · ebiklogistics.com · Moving Cargo. Connecting Business.
        </div>
      </footer>
    </div>
  );
}
